// Copyright (c) 2026 Yunus YILDIZ — SPDX-License-Identifier: BUSL-1.1

import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '@/lib/auth/useAuth'
import { AuthLayout } from '@/components/layout/AuthLayout'
import { MicrosoftSignInButton } from '@/components/auth/MicrosoftSignInButton'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Alert } from '@/components/ui/Alert'
import { ROUTES } from '@/constants/routes'
import { friendlyError } from '@/lib/utils/errorHandler'

export function RegisterPage() {
  const { register } = useAuth()
  const navigate = useNavigate()

  const [form, setForm] = useState({ companyName: '', name: '', email: '', password: '' })
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }))
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      await register(form)
      navigate(ROUTES.DASHBOARD, { replace: true })
    } catch (err) {
      setError(friendlyError(err))
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthLayout>
      <div className="mb-8 text-center">
        <h1 className="text-xl font-bold text-ink">Create your account</h1>
        <p className="mt-1 text-sm text-ink-muted">Start with Etapsky Portal — no credit card required</p>
      </div>

      <form onSubmit={(e) => void handleSubmit(e)} className="card p-6 space-y-4">
        {error && <Alert variant="error">{error}</Alert>}

        <Input label="Company name" name="companyName" value={form.companyName} onChange={handleChange} placeholder="Acme Corp" required />
        <Input label="Your name" name="name" value={form.name} onChange={handleChange} placeholder="Jane Smith" required />
        <Input label="Work email" name="email" type="email" value={form.email} onChange={handleChange} placeholder="jane@acmecorp.com" required autoComplete="email" />
        <Input label="Password" name="password" type="password" value={form.password} onChange={handleChange} placeholder="Min 8 chars, 1 uppercase, 1 number" required autoComplete="new-password" />

        <Button type="submit" variant="primary" className="w-full" loading={loading}>
          Create account
        </Button>

        <div className="relative py-1">
          <div className="absolute inset-0 flex items-center" aria-hidden>
            <span className="w-full border-t border-line" />
          </div>
          <div className="relative flex justify-center text-xs font-medium">
            <span className="bg-[var(--raised)] px-3 text-ink-muted">or</span>
          </div>
        </div>

        <MicrosoftSignInButton disabled={loading} onError={setError} />

        <p className="text-xs text-ink-muted text-center">
          By signing up you agree to our{' '}
          <a href="https://etapsky.com/legal/terms" className="text-brand hover:underline">Terms of Service</a>
          {' '}and{' '}
          <a href="https://etapsky.com/legal/privacy" className="text-brand hover:underline">Privacy Policy</a>.
        </p>
      </form>

      <p className="mt-4 text-center text-sm text-ink-muted">
        Already have an account?{' '}
        <Link to={ROUTES.LOGIN} className="text-brand hover:underline font-medium">Sign in</Link>
      </p>
    </AuthLayout>
  )
}
